import moment from "moment-timezone";
import { GetTransactionsResponse } from "../types/transactions";
import { isQuickSaleTransaction } from "../types/quickSale";
import { getLineDiscountDollars } from "./discountUtils";

function sumAppointmentTransaction(tx: GetTransactionsResponse): number {
  const bookedServices = tx.line_items.booked_services || [];
  const servicesTotal = bookedServices.reduce(
    (total, service) =>
      total +
      service.unit_price -
      getLineDiscountDollars(service, service.unit_price) +
      (service.tip || 0),
    0,
  );
  return servicesTotal + sumProducts(tx);
}

/** Products at their unit price × quantity, net of line discounts. */
function sumProducts(tx: GetTransactionsResponse): number {
  return (tx.line_items.products || []).reduce((total, product) => {
    const subtotal = product.unit_price * product.quantity;
    return total + subtotal - getLineDiscountDollars(product, subtotal);
  }, 0);
}

function sumQuickSaleTransaction(tx: GetTransactionsResponse): number {
  return sumProducts(tx);
}

export interface ClientDetailStats {
  totalVisits: number;
  lastVisitDisplay: string | null;
  totalSpent: number;
}

/**
 * Derives overview stats from client transactions (same pricing logic as the activity timeline).
 */
export function computeClientDetailStats(
  transactions: GetTransactionsResponse[] | undefined,
  lastAppointmentIso: string | undefined,
  timezone: string,
): ClientDetailStats {
  const txs = transactions ?? [];
  let totalVisits = 0;
  let totalSpent = 0;
  let latestStart: string | null = null;

  const considerDate = (iso: string | undefined) => {
    if (!iso) return;
    if (!latestStart || new Date(iso) > new Date(latestStart)) {
      latestStart = iso;
    }
  };

  for (const tx of txs) {
    if (isQuickSaleTransaction(tx)) {
      totalSpent += sumQuickSaleTransaction(tx);
    } else {
      totalVisits += 1;
      totalSpent += sumAppointmentTransaction(tx);
      const booked = tx.line_items.booked_services || [];
      considerDate(booked[0]?.start_dt);
    }
  }

  considerDate(lastAppointmentIso);

  const lastVisitDisplay = latestStart
    ? moment.utc(latestStart).tz(timezone).format("MMM D, YYYY")
    : null;

  return { totalVisits, lastVisitDisplay, totalSpent };
}
